// Audit logging helpers — every action logged to master_audit_logs
// Uses SECURITY DEFINER RPC to bypass RLS (Agent PWA runs as anon role)

import { supabase } from './supabase';
import { AgentSession } from './agent-auth';

export type AuditAction =
  | 'agent_login' |'agent_logout' |'agent_login_failed' |'qr_generated' |'phone_subscription_created' |'qr_validated' |'phone_validated' |'ref_validated' |'qr_printed' |'qr_downloaded' |'offline_sync_completed';

export interface AuditDetails {
  phone?: string;
  gps?: { lat: number; lng: number } | null;
  device?: string;
  method?: string;
  reference_id?: string;
  batch_id?: string;
  quantity?: number;
  plan?: string;
  union?: string;
  state?: string;
  error?: string;
  result?: string;
  sync_status?: 'synced' | 'pending';
  plan_name?: string;
  status?: string;
  [key: string]: unknown;
}

export async function logAudit(
  action: AuditAction,
  session: AgentSession | null,
  details: AuditDetails = {}
): Promise<string | null> {
  try {
    const device =
      typeof navigator !== 'undefined' ? navigator.userAgent.substring(0, 200) : 'unknown';

    // Parse record_id safely — must be a valid UUID or null
    let recordId: string | null = null;
    const rawRef = details.reference_id || details.batch_id || null;
    if (rawRef) {
      // Only pass as UUID if it looks like one; otherwise store in new_value only
      const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      if (uuidPattern.test(rawRef)) {
        recordId = rawRef;
      }
    }

    const newValue = {
      ...details,
      device: details.device || device,
      agent_role: session?.role || null,
      union_id: session?.union_id || null,
      state_id: session?.state_id || null,
      timestamp: new Date().toISOString(),
      sync_status: details.sync_status || 'synced',
      // Store agent_id in new_value as a fallback lookup key.
      // The get_agent_activity_history RPC uses this when actor_id is NULL
      // (which happens when the agent's UUID is not in user_profiles).
      agent_id: session?.id || null,
    };

    // Use SECURITY DEFINER RPC to bypass RLS (anon role has no auth.uid())
    const { data, error } = await supabase.rpc('log_agent_activity', {
      p_actor_id: session?.id || null,
      p_actor_phone: session?.phone || null,
      p_action: action,
      p_module: 'agent_pwa',
      p_record_id: recordId,
      p_record_label: details.phone || details.reference_id || null,
      p_new_value: newValue,
    });

    if (error) {
      console.warn('[Audit] RPC log_agent_activity failed:', error.message, error.hint);
      return null;
    }

    return data as string | null;
  } catch (err) {
    console.warn('[Audit] Failed to log action:', action, err);
    return null;
  }
}

export async function getGpsCoords(): Promise<{ lat: number; lng: number } | null> {
  return new Promise((resolve) => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      resolve(null);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => resolve(null),
      { timeout: 5000, maximumAge: 60000 }
    );
  });
}